/**
 * Contact Form Modal Validation Schema
 * Zod schema that matches ContactFormModal field names exactly
 */

import { z } from 'zod';

export const contactFormModalSchema = z.object({
  // Basic Information
  name: z
    .string()
    .min(1, 'vendor.validation.nameRequired')
    .max(100, 'vendor.validation.nameMax'),
  isPrimary: z.boolean(),
  jobTitle: z
    .string()
    .min(1, 'vendor.validation.jobTitleRequired')
    .max(100, 'vendor.validation.jobTitleMax'),

  // Company ID - optional in schema (used by MasterDataContactFormModal, not vendor ContactFormModal)
  // Required validation handled by Controller rules in create mode
  companyId: z.number().nullable().optional(),

  // #92 multi-affiliation list (used by MasterDataContactFormModal only).
  // Optional so the vendor/customer-scoped ContactFormModal keeps validating.
  // Row completeness (companyId chosen) is enforced in onSubmit, matching the
  // companyRequired pattern above.
  affiliations: z
    .array(
      z.object({
        companyType: z.enum(['vendor', 'customer']),
        companyId: z.number().nullable(),
        roleTitle: z.string().optional().or(z.literal('')),
      })
    )
    .optional(),

  // Phone
  phoneCountryCode: z.string().min(1, 'vendor.validation.countryCodeRequired'),
  phoneNumber: z
    .string()
    .min(1, 'vendor.validation.phoneRequired')
    .regex(/^\d{6,15}$/, 'vendor.validation.phoneFormat'),
  phoneExtension: z.string().optional(),

  // Mobile
  mobileCountryCode: z.string().optional(),
  mobileNumber: z
    .string()
    .regex(/^\d{6,15}$/, 'vendor.validation.mobileFormat')
    .optional()
    .or(z.literal('')),

  // Communication
  fax: z.string().optional().or(z.literal('')),
  email: z
    .string()
    .min(1, 'vendor.validation.emailRequired')
    .email('vendor.validation.emailInvalidFormat')
    .max(100, 'vendor.validation.emailMax'),

  // Address - using IDs instead of string names
  // All address fields are optional for contacts
  countryId: z.number().nullable().optional(),
  stateId: z.number().nullable().optional(),
  cityId: z.number().nullable().optional(),
  postcode: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
});

export type ContactFormModalData = z.infer<typeof contactFormModalSchema>;
